import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { useAppDispatch } from "../../redux";
import {
  getAllCampaignSalesDataAction,
  getSalesGeneratingCampaignsAction,
  getStoreCampaignMetricsAction,
} from "../../redux/dashboard/analytics.slice";

import { useAnalyticsFilter } from "../../context/AnalyticsFilterContext";

import Icon from "../../assets/svg";
import dotBlue from "../../assets/svg/dot_blue.svg";
import graphNewIcon from "../../assets/svg/graph-new.svg";
import presentationGraphIcon from "../../assets/svg/presentation-graph.svg";

import Table from "../../components/table";
import Loader from "../../components/loader";
import NoData from "../../components/no-data";

import SalesContributionChart from "./sales-contribution-chart";
import TrafficChart from "./store-traffic-chart";
import ExportData from "./export-data";
import { handleExportPdf, handleExportPng } from "../../utils/functions";
import AnalyticsFilter from "./analytics-filter";

interface CampaignDetails {
  campaign_id: string;
  campaign_title: string;
  total_recipients: number;
  total_purchases: number;
  sum_purchases: string;
}

interface CampaignMetrics {
  name: string;
  value: string;
  status: string;
  percentage: string;
}

const CampaignAnalytics = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    isFetchingStoreCampaignMetrics,
    storeCampaignMetrics,
    isFetchingAllCampaignSalesData,
    campaignSalesData,
    isFetchingSalesGeneratingCampaigns,
    salesGeneratingCampaigns,
  } = useSelector((state: any) => state.analyticsSlice);

  const { duration } = useAnalyticsFilter();

  const [showMetricsDropDown, setShowMetricsDropDown] = useState(false);
  const [showSalesDropDown, setShowSalesDropDown] = useState(false);

  const metricRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getSalesGeneratingCampaignsAction());
    dispatch(getAllCampaignSalesDataAction());
  }, []);

  useEffect(() => {
    if (duration) {
      const params = {
        range: duration === "12month" ? "5months" : duration,
      };
      getCampaignStoreMetrics(params);
    }
  }, [duration]);

  const getCampaignStoreMetrics = (
    params: { range: string } | { start: string; end: string }
  ) => {
    dispatch(getStoreCampaignMetricsAction(params));
  };

  const tableHeaders = [
    "Campaign Title",
    "No. Of Recipients",
    "No. Of Purchases",
    "Sales From Campaign",
  ];

  const tableExportOptions = [
    {
      name: "PDF",
      action: () =>
        tableRef.current &&
        handleExportPdf(tableRef.current, "konvasAI-campaign-sales.pdf"),
    },
    {
      name: "Image(PNG)",
      action: () =>
        tableRef.current &&
        handleExportPng(tableRef.current, "konvasAI-campaign-sales.png"),
    },
  ];

  return (
    <div>
      <AnalyticsFilter
        ref={metricRef}
        disableActions={!showMetricsDropDown}
        getMetrics={getCampaignStoreMetrics}
      />
      <div className="analytics_details">
        <div className="left">
          <div className="dropdown">
            <div
              className={classNames("metric_charts", {
                focused: showMetricsDropDown,
              })}
              onClick={() => setShowMetricsDropDown((prev) => !prev)}
            >
              <div className="text_right">
                <img src={graphNewIcon} alt="" />
                <p>
                  Campaign Metrics{"  "}
                  <span>
                    See total campaign sent, subscriptions, revenue per clicks,
                    reach and clicks
                  </span>
                </p>
              </div>
              <Icon name="chevronDown" />
            </div>

            {showMetricsDropDown && (
              <div className="dropdown_content">
                {isFetchingStoreCampaignMetrics ? (
                  <Loader />
                ) : !storeCampaignMetrics ||
                  storeCampaignMetrics?.length === 0 ? (
                  <NoData text="No campaign metrics to show" />
                ) : (
                  <div className="content_details" ref={metricRef}>
                    {storeCampaignMetrics.map((metric: CampaignMetrics) => (
                      <div className="metric_card" key={metric?.name}>
                        <p>{metric?.name}</p>
                        <div className="bottom">
                          <p>{metric?.value}</p>
                          <div
                            className={classNames("tag", {
                              decrease: metric?.status === "decrease",
                              increase: metric?.status === "increase",
                            })}
                          >
                            {(metric?.status === "decrease" ||
                              metric?.status === "increase") && (
                              <Icon
                                name={
                                  metric?.status === "decrease"
                                    ? "arrowDown"
                                    : "arrowUp"
                                }
                              />
                            )}

                            <p>{metric?.percentage}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="dropdown">
            <div
              className={classNames("metric_charts mt", {
                focused: showSalesDropDown,
              })}
              onClick={() => setShowSalesDropDown((prev) => !prev)}
            >
              <div className="text_right">
                <img src={presentationGraphIcon} alt="" />
                <p>
                  Graphs and Charts{"  "}
                  <span>
                    See Konvas AI contribution to overall sales and store
                    traffic.
                  </span>
                </p>
              </div>
              <Icon name="chevronDown" />
            </div>
            {showSalesDropDown && (
              <div className="dropdown_content">
                <div className="content_details">
                  <SalesContributionChart />
                  <TrafficChart />
                </div>
              </div>
            )}
          </div>

          <div className="table_container">
            <div className="top_bar">
              <p>Campaign Details Table</p>
              <ExportData size="small" exportOptions={tableExportOptions} />
            </div>
            <div ref={tableRef}>
              <Table
                isLoading={isFetchingAllCampaignSalesData}
                headers={tableHeaders}
                noRecord={campaignSalesData?.length < 1}
              >
                {campaignSalesData?.map((row: CampaignDetails) => (
                  <tr key={row?.campaign_id}>
                    <td>
                      <div className="title">
                        <p className="">{row?.campaign_title}</p>
                      </div>
                    </td>

                    <td>
                      <p>{row?.total_recipients}</p>
                    </td>
                    <td>
                      <p>{row?.total_purchases}</p>
                    </td>
                    <td>
                      <p>{row?.sum_purchases}</p>
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          </div>
        </div>
        <div className="right">
          <p className="heading">Sales-generating campaigns</p>
          {isFetchingSalesGeneratingCampaigns ? (
            <Loader />
          ) : !salesGeneratingCampaigns ||
            salesGeneratingCampaigns?.length === 0 ? (
            <NoData text="No sales generating campaigns yet" />
          ) : (
            <>
              {" "}
              {salesGeneratingCampaigns.map((data: any) => (
                <div
                  className="campaign_data"
                  onClick={() =>
                    history.push(`/campaigns?campaignId=${data?.id}`)
                  }
                >
                  <img src={dotBlue} alt="" />
                  <div className="data">
                    <p>{data?.campaign_title}</p>
                    {/* <p>
              <span>100</span> Open, <span>20</span> Clicks, <span>5</span>{" "}
              Sales
            </p> */}
                    <p>
                      <span>{data?.sale}</span> Sales(s)
                    </p>
                  </div>
                </div>
              ))}
              {/* <p className="more">See More...</p> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
